import { Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';

type Props = {
  color?: string;
  onColorChange: (color: string) => void;
};

export const ColorPicker = (props: Props) => {
  const [open, setOpen] = useState(false);
  const [hexInput, setHexInput] = useState(props.color || '#6366f1');

  // Sync hexInput when color changes externally
  useEffect(() => {
    setHexInput(props.color || '#6366f1');
  }, [props.color]);

  const handlePresetClick = (color: string) => {
    props.onColorChange(color);
    setHexInput(color);
    setOpen(false);
  };

  const handleHexChange = (value: string) => {
    setHexInput(value);
    if (/^#[\da-f]{6}$/i.test(value)) {
      props.onColorChange(value);
    }
  };

  return (
    <Popover
      onOpenChange={value => {
        setOpen(value);
        if (value) {
          setHexInput(props.color || '#6366f1');
        }
      }}
      open={open}
    >
      <PopoverTrigger asChild>
        <button
          aria-label="Pick color"
          className="group relative size-6 shrink-0 rounded-full border border-border shadow-sm transition-shadow duration-150 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          style={{ backgroundColor: props.color || '#6366f1' }}
          type="button"
        >
          <Pencil className="absolute inset-0 m-auto h-3 w-3 text-white opacity-0 drop-shadow-sm transition-opacity duration-150 group-hover:opacity-100" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-3"
        onOpenAutoFocus={e => e.preventDefault()}
        side="top"
      >
        <div className="flex items-center gap-2">
          {PRESET_COLORS.map(color => (
            <button
              aria-label={`Select color ${color}`}
              className="size-5 rounded-full transition-transform duration-100 hover:scale-110"
              key={color}
              onClick={() => handlePresetClick(color)}
              style={{
                backgroundColor: color,
                outline:
                  props.color === color ? `2px solid ${color}` : undefined,
                outlineOffset: props.color === color ? '2px' : undefined
              }}
              type="button"
            />
          ))}
          <span className="mx-2 h-6 w-px shrink-0 bg-border" />
          <label className="relative flex size-6 shrink-0 cursor-pointer items-center">
            <span
              className="block size-5 rounded-full"
              style={{
                background:
                  'conic-gradient(rgb(235, 87, 87), rgb(242, 201, 76), rgb(76, 183, 130), rgb(78, 167, 252), rgb(250, 96, 122))'
              }}
            />
            <input
              className="absolute inset-0 cursor-pointer opacity-0"
              onChange={e => {
                props.onColorChange(e.target.value);
                setHexInput(e.target.value);
              }}
              type="color"
              value={props.color || '#6366f1'}
            />
          </label>
          <Input
            className="h-6 w-20 font-mono text-xs"
            maxLength={7}
            onChange={e => handleHexChange(e.target.value)}
            placeholder="#000000"
            value={hexInput}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const PRESET_COLORS = [
  '#5e6ad2',
  '#26b5ce',
  '#4cb782',
  '#f0bf00',
  '#f2994a',
  '#f7c8c1',
  '#eb5757'
];
